/**
 * LiveRSVPHero — iter172 (Q-a: "next upcoming session" hero).
 *
 * Picks the single next session with status='rsvp_open' or 'needs_guests'
 * (i.e. RSVP still live), then shows a scoreboard of:
 *   IN (confirmed) · OUT (explicit) · WAITING (waitlist) · PENDING (no response)
 *
 * Updates via the same `sessions` array the parent already keeps fresh via SSE,
 * so no extra fetches required.
 *
 * Admin/co-admin sees a "Nudge waiting" button that POSTs /sessions/{id}/nudge-pending
 * with a server-side cooldown driven by `group.rsvp_nudge_cooldown_minutes` (default 30).
 */
import { useMemo, useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { serverNow } from '../lib/serverClock';
import { BellRing, Clock, CheckCircle2, XCircle, UserPlus, Hourglass } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';

function _msToText(ms) {
  if (ms <= 0) return 'now';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem ? `${h}h ${rem}m` : `${h}h`;
}

const Bucket = ({ label, count, icon: Icon, tone, testId }) => (
  <div
    data-testid={testId}
    className={`flex-1 min-w-[64px] rounded-2xl border-2 p-2.5 flex flex-col items-center justify-center tabular-nums transition ${tone}`}
  >
    <Icon className="w-4 h-4 mb-1 opacity-80" />
    <p className="font-display text-2xl font-bold leading-none">{count}</p>
    <p className="text-xs font-bold uppercase tracking-widest opacity-70 mt-0.5">{label}</p>
  </div>
);

export default function LiveRSVPHero({ ctx }) {
  const { group, sessions, user, isAdmin } = ctx || {};
  const isCoAdmin = (group?.co_admin_ids || []).includes(user?.id);
  const canNudge = isAdmin || isCoAdmin;

  // Tick fast (1s) when any session's RSVP opens within 2 minutes so the hero
  // appears the exact second it opens; otherwise refresh once a minute.
  const [tickCount, setTick] = useState(0);
  useEffect(() => {
    const nearOpen = (sessions || []).some((s) => {
      if (s.status !== 'scheduled' || !s.rsvp_opens_at || s.rsvp_locked) return false;
      const d = new Date(s.rsvp_opens_at).getTime() - serverNow();
      return d > 0 && d < 120000;
    });
    const id = setInterval(() => setTick((n) => n + 1), nearOpen ? 1000 : 60000);
    return () => clearInterval(id);
  }, [sessions, tickCount]);

  const next = useMemo(() => {
    if (!sessions || sessions.length === 0) return null;
    const now = serverNow();
    // iter265 — treat a session as open the instant rsvp_opens_at passes
    // (server clock), without waiting for the cron to flip `status`.
    const isOpen = (s) => {
      if (['rsvp_open', 'needs_guests'].includes(s.status)) return true;
      if (s.status === 'scheduled' && s.rsvp_opens_at && !s.rsvp_locked) {
        return new Date(s.rsvp_opens_at).getTime() <= now;
      }
      return false;
    };
    return [...sessions]
      .filter(isOpen)
      .filter((s) => {
        if (!s.rsvp_closes_at) return true;
        return new Date(s.rsvp_closes_at).getTime() > now;
      })
      .sort((a, b) => {
        const ta = a.rsvp_closes_at ? new Date(a.rsvp_closes_at).getTime() : Number.POSITIVE_INFINITY;
        const tb = b.rsvp_closes_at ? new Date(b.rsvp_closes_at).getTime() : Number.POSITIVE_INFINITY;
        return ta - tb;
      })[0] || null;
  }, [sessions, tickCount]);

  const [nudging, setNudging] = useState(false);

  // iter173 — "Spot Just Opened" alert (sound + tab title flash) when someone
  // in the IN bucket drops to OUT. Only fires for OTHER viewers (not the
  // person who themselves clicked Out), and only when there are waitlist or
  // pending members who could fill the spot.
  const prevConfirmedRef = useRef(null);
  const titleFlashRef = useRef(null);
  useEffect(() => {
    if (!next) {
      prevConfirmedRef.current = null;
      return;
    }
    const currConfirmed = new Set(next.confirmed_ids || (next.rsvp_in_ids || []).slice(0, next.max_players || 6));
    const prev = prevConfirmedRef.current;
    prevConfirmedRef.current = currConfirmed;
    if (!prev) return; // first render — nothing to compare
    // Find IDs that dropped from IN.
    const dropped = [];
    prev.forEach((uid) => { if (!currConfirmed.has(uid)) dropped.push(uid); });
    if (dropped.length === 0) return;
    // Don't fire if it's the current user who dropped themselves out.
    if (dropped.length === 1 && dropped[0] === user?.id) return;
    // Are there potential takers? (waitlist + pending)
    const members = group?.member_ids || [];
    const wIds = next.waitlist_ids || [];
    const oIds = next.rsvp_out_ids || [];
    const pendingExists = members.some((uid) => !currConfirmed.has(uid) && !wIds.includes(uid) && !oIds.includes(uid));
    if (wIds.length === 0 && !pendingExists) return;
    // Don't alert if the user is already IN this session (they have their spot).
    if (currConfirmed.has(user?.id)) return;

    // 1) Beep via Web Audio API — no asset to ship.
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (AC) {
        const ctxA = new AC();
        const o = ctxA.createOscillator();
        const g = ctxA.createGain();
        o.connect(g);
        g.connect(ctxA.destination);
        o.type = 'sine';
        o.frequency.setValueAtTime(880, ctxA.currentTime);
        g.gain.setValueAtTime(0.0001, ctxA.currentTime);
        g.gain.exponentialRampToValueAtTime(0.12, ctxA.currentTime + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctxA.currentTime + 0.35);
        o.start();
        o.stop(ctxA.currentTime + 0.36);
        // chained second beep for a "ding-ding" feel
        const o2 = ctxA.createOscillator();
        const g2 = ctxA.createGain();
        o2.connect(g2);
        g2.connect(ctxA.destination);
        o2.type = 'sine';
        o2.frequency.setValueAtTime(1175, ctxA.currentTime + 0.18);
        g2.gain.setValueAtTime(0.0001, ctxA.currentTime + 0.18);
        g2.gain.exponentialRampToValueAtTime(0.12, ctxA.currentTime + 0.2);
        g2.gain.exponentialRampToValueAtTime(0.0001, ctxA.currentTime + 0.55);
        o2.start(ctxA.currentTime + 0.18);
        o2.stop(ctxA.currentTime + 0.56);
        setTimeout(() => { try { ctxA.close(); } catch { /* ignore */ } }, 800);
      }
    } catch { /* ignore — audio may be blocked until user interacts with the tab */ }

    // 2) Browser tab title flash until tab gains focus.
    const origTitle = document.title;
    if (titleFlashRef.current) clearInterval(titleFlashRef.current);
    let toggle = true;
    titleFlashRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') {
        clearInterval(titleFlashRef.current);
        titleFlashRef.current = null;
        document.title = origTitle;
        return;
      }
      document.title = toggle ? `\u26a1 Spot open \u2014 ${next.title || 'Session'}` : origTitle;
      toggle = !toggle;
    }, 1100);

    // 3) Optional toast (in-app), small and unobtrusive.
    try {
      toast(`\u26a1 A spot just opened in "${next.title || 'Session'}"`, { duration: 6000 });
    } catch { /* ignore */ }

    // Cleanup: stop flashing when the user comes back.
    const onFocus = () => {
      // Only act when the tab actually becomes visible (the same event also
      // fires on hide; we want to keep flashing while hidden).
      if (document.visibilityState !== 'visible') return;
      if (titleFlashRef.current) {
        clearInterval(titleFlashRef.current);
        titleFlashRef.current = null;
        document.title = origTitle;
      }
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
    document.addEventListener('visibilitychange', onFocus);
    window.addEventListener('focus', onFocus);
    return () => {
      if (titleFlashRef.current) {
        clearInterval(titleFlashRef.current);
        titleFlashRef.current = null;
      }
      document.title = origTitle;
      document.removeEventListener('visibilitychange', onFocus);
      window.removeEventListener('focus', onFocus);
    };
  }, [next, group?.member_ids, user?.id]);

  // All hooks must come BEFORE any conditional return (Rules of Hooks).
  const memberMap = useMemo(() => {
    const m = {};
    (group?.members || []).forEach((u) => { m[u.id] = u.name; });
    return m;
  }, [group?.members]);

  if (!next) return null;

  const members = group?.member_ids || [];
  const confirmedIds = next.confirmed_ids || (next.rsvp_in_ids || []).slice(0, next.max_players || 6);
  const waitlistIds = next.waitlist_ids || [];
  const outIds = next.rsvp_out_ids || [];
  const decided = new Set([...confirmedIds, ...waitlistIds, ...outIds]);
  const pendingIds = members.filter((uid) => !decided.has(uid));

  const closeMs = next.rsvp_closes_at ? new Date(next.rsvp_closes_at).getTime() - Date.now() : null;
  const closeLabel = closeMs == null ? null : closeMs <= 0 ? 'Closing now' : `Closes in ${_msToText(closeMs)}`;
  const maxP = next.max_players || 6;

  const handleNudge = async () => {
    if (nudging || pendingIds.length === 0) return;
    setNudging(true);
    try {
      const res = await api.post(`/sessions/${next.id}/nudge-pending`);
      toast.success(res.data?.message || 'Nudged');
    } catch (e) {
      const status = e.response?.status;
      const msg = e.response?.data?.detail || 'Failed to nudge';
      if (status === 429) toast.error(msg);
      else toast.error(msg);
    } finally {
      setNudging(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 via-background to-background p-4 sm:p-5 space-y-3"
      data-testid="live-rsvp-hero"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-primary flex items-center gap-1.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
            </span>
            LIVE RSVP
          </p>
          <h3 className="font-display text-base sm:text-lg font-bold truncate" data-testid="live-rsvp-title">
            {next.title || 'Session'}
          </h3>
          <p className="text-xs text-muted-foreground">
            {next.date} · {next.start_time || ''}{closeLabel ? ` · ${closeLabel}` : ''}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Spots</p>
          <p className="text-xl font-bold tabular-nums" data-testid="live-rsvp-spots">
            {confirmedIds.length}<span className="text-sm opacity-50">/{maxP}</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
        <Bucket
          testId="rsvp-bucket-in"
          label="In"
          count={confirmedIds.length}
          icon={CheckCircle2}
          tone="border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
        />
        <Bucket
          testId="rsvp-bucket-out"
          label="Out"
          count={outIds.length}
          icon={XCircle}
          tone="border-rose-500/30 bg-rose-500/5 text-rose-700 dark:text-rose-400"
        />
        <Bucket
          testId="rsvp-bucket-waiting"
          label="Waiting"
          count={waitlistIds.length}
          icon={Hourglass}
          tone="border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400"
        />
        <Bucket
          testId="rsvp-bucket-pending"
          label="Pending"
          count={pendingIds.length}
          icon={Clock}
          tone="border-border bg-muted/40 text-muted-foreground"
        />
      </div>

      {/* Live name rows */}
      <div className="space-y-1.5 text-xs">
        {confirmedIds.length > 0 && (
          <div data-testid="rsvp-names-in">
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600 mr-1.5">IN</span>
            <span className="text-foreground">
              {confirmedIds.map((uid) => memberMap[uid] || 'Unknown').join(' \u00b7 ')}
            </span>
          </div>
        )}
        {outIds.length > 0 && (
          <div data-testid="rsvp-names-out">
            <span className="text-xs font-bold uppercase tracking-widest text-rose-600 mr-1.5">OUT</span>
            <span className="text-muted-foreground line-through decoration-rose-500/50">
              {outIds.map((uid) => memberMap[uid] || 'Unknown').join(' \u00b7 ')}
            </span>
          </div>
        )}
        {waitlistIds.length > 0 && (
          <div data-testid="rsvp-names-waiting">
            <span className="text-xs font-bold uppercase tracking-widest text-amber-600 mr-1.5">WAITING</span>
            <span className="text-foreground">
              {waitlistIds.map((uid, i) => `${i + 1}. ${memberMap[uid] || 'Unknown'}`).join(' \u00b7 ')}
            </span>
          </div>
        )}
        {pendingIds.length > 0 && (
          <div data-testid="rsvp-names-pending">
            <span className="text-xs font-bold uppercase tracking-widest text-muted-foreground mr-1.5">PENDING</span>
            <span className="text-muted-foreground italic">
              {pendingIds.map((uid) => memberMap[uid] || 'Unknown').join(' \u00b7 ')}
            </span>
          </div>
        )}
      </div>

      {/* Nudge button */}
      {canNudge && pendingIds.length > 0 && (
        <Button
          onClick={handleNudge}
          disabled={nudging}
          data-testid="live-rsvp-nudge-btn"
          variant="outline"
          className="w-full h-10 rounded-full text-xs font-bold border-2 border-primary/30 hover:bg-primary/10"
        >
          <BellRing className="w-4 h-4 mr-1.5" />
          {nudging ? 'Sending...' : `Nudge ${pendingIds.length} pending`}
        </Button>
      )}

      {/* Self-RSVP shortcuts when user hasn't decided */}
      {!confirmedIds.includes(user?.id)
        && !outIds.includes(user?.id)
        && !waitlistIds.includes(user?.id) && (
        <div className="flex gap-2" data-testid="live-rsvp-self-actions">
          <Button
            onClick={() => ctx?.handleRsvp?.(next.id, 'in')}
            className="flex-1 h-10 rounded-full text-xs font-bold bg-emerald-600 hover:bg-emerald-700"
            data-testid="live-rsvp-me-in"
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
            I'm In
          </Button>
          <Button
            variant="outline"
            onClick={() => ctx?.handleRsvp?.(next.id, 'out')}
            className="flex-1 h-10 rounded-full text-xs font-bold border-2 border-rose-500/40 text-rose-600 hover:bg-rose-500/10"
            data-testid="live-rsvp-me-out"
          >
            <XCircle className="w-3.5 h-3.5 mr-1" />
            I'm Out
          </Button>
        </div>
      )}

      {/* Currently-decided members can flip with a quick toggle */}
      {(confirmedIds.includes(user?.id) || waitlistIds.includes(user?.id)) && (
        <Button
          onClick={() => ctx?.handleRsvp?.(next.id, 'out')}
          variant="ghost"
          className="w-full h-9 rounded-full text-xs font-bold text-muted-foreground hover:text-rose-600"
          data-testid="live-rsvp-flip-out"
        >
          <XCircle className="w-3 h-3 mr-1" />
          Change my mind — mark Out
        </Button>
      )}
      {outIds.includes(user?.id) && (
        <Button
          onClick={() => ctx?.handleRsvp?.(next.id, 'in')}
          variant="ghost"
          className="w-full h-9 rounded-full text-xs font-bold text-muted-foreground hover:text-emerald-600"
          data-testid="live-rsvp-flip-in"
        >
          <UserPlus className="w-3 h-3 mr-1" />
          I'll come — mark In
        </Button>
      )}
    </motion.div>
  );
}

